import {Button} from '@mantine/core';
import React, {useEffect, useRef, useState} from 'react';
import {MentionsInput, Mention} from 'react-mentions';

import {signal} from "@preact/signals-react";
import {useSignals} from '@preact/signals-react/runtime';
import {useIsMobile} from '@/utils/user_agent';
import {useSelectorWithRefresh} from '@/store/kysely_module';
import {useCurrentWorkspaceId} from '@/store/modules/workspaces_module';
import {CommentFormCustom} from './comment_form_custom';

export {CommentFormCustom};

type CommentFormProps = {
    onSubmit: (text: string) => Promise<void>;
    initialValue?: string;
    onCancel?: () => void;
    isReply?: boolean;
    onInitialFocus?: () => string;
    newTimestamp?: {timestamp: string} | null;
    scrollIntoView?: {scrollIntoView: boolean};
    autoFocus?: boolean;
};

export const signalCommentTextareaIsFocused = signal(false);

export const CommentForm = (props: CommentFormProps) => {
    useSignals();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const workspaceId = useCurrentWorkspaceId();

    const [draft, setDraft] = useState(props.initialValue || '');

    const submitButtonText = props.isReply ? 'Add Reply' : 'Submit';

    const isMobile = useIsMobile();

    const [workspaceUsers] = useSelectorWithRefresh('getUsersForWorkspace', {
        workspace_id: workspaceId,
        excludeSelf: false
    }, [], {workspace_id: workspaceId});

    const mentionUsers = workspaceUsers.map((user: any) => ({
        id: user.id,
        display: user.name || user.email || 'Unknown User'
    }));

    useEffect(() => {
        if (props.scrollIntoView && !isMobile) {
            setTimeout(() => {
                window.scrollBy({ top: (textareaRef?.current?.getBoundingClientRect().top || 0) - 400, behavior: 'smooth' });
                textareaRef.current?.focus();
                if (textareaRef.current) {
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 100);
        }
    }, [props.scrollIntoView]);

    useEffect(() => {
        if ((props.initialValue || props.isReply) && textareaRef.current) {
            setTimeout(() => {
                textareaRef.current?.focus();
                if (textareaRef.current) {
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    }, [props.initialValue, props.isReply]);

    useEffect(() => {
        if (props.autoFocus && textareaRef.current) {
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 0);
        }
    }, [props.autoFocus]);

    const handleTextareaClick = (event: React.MouseEvent) => {
        event.preventDefault();

        document.querySelector('main')!.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        if (props.newTimestamp) {
            setDraft(`${draft ? `${draft}\n` : ''}${props.newTimestamp.timestamp} `);
            setTimeout(() => {
                textareaRef.current?.focus();
                if (textareaRef.current) {
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    }, [props.newTimestamp]);

    const stripMentionMarkup = (text: string) => {
        return text.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, '@$1');
    };

    return (
        <form
            id='comment-form'
            // reply={props.isReply}
            onSubmit={async (e) => {
                e.preventDefault();
                const cleanText = stripMentionMarkup(draft);
                await props.onSubmit(cleanText);
                setDraft('');
            }}
        >
            <MentionsInput
                inputRef={textareaRef}
                value={draft}
                onChange={(event: any, newValue: string) => {
                    setDraft(newValue);
                }}
                onClick={handleTextareaClick}
                style={{
                    maxWidth: '600px',
                    '&multiLine': {
                        control: {
                            minHeight: 120,
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            fontSize: '14px',
                            lineHeight: 1.4,
                            backgroundColor: 'var(--mantine-color-dark-6)',
                        },
                        highlighter: {
                            opacity: 0,
                            padding: '8px 12px',
                            border: '1px solid transparent',
                            fontSize: '14px',
                            lineHeight: 1.4,
                        },
                        input: {
                            padding: '8px 12px',
                            border: '1px solid transparent',
                            outline: 0,
                            fontSize: '14px',
                            lineHeight: 1.4,
                            color: 'var(--mantine-color-text)',
                            backgroundColor: 'transparent',
                        }
                    },
                    suggestions: {
                        backgroundColor: 'var(--mantine-color-dark-6)',
                        border: '1px solid var(--mantine-color-dark-4)',
                        borderRadius: '4px',
                        fontSize: '14px',
                        marginTop: '20px',
                        marginLeft: '-10px',
                        list: {
                            backgroundColor: 'var(--mantine-color-dark-6)',
                        },
                        item: {
                            padding: '8px 12px',
                            color: 'var(--mantine-color-text)',
                            fontSize: '14px',
                            '&focused': {
                                backgroundColor: 'var(--mantine-color-dark-5)',
                            }
                        }
                    }
                }}
                onFocus={() => {
                    signalCommentTextareaIsFocused.value = true;
                }}
                onBlur={() => {
                    setTimeout(() => {
                        signalCommentTextareaIsFocused.value = false;
                    }, 500);
                }}
                placeholder="Write a comment..."
                a11ySuggestionsListLabel="Suggested mentions"
            >
                <Mention
                    trigger="@"
                    data={mentionUsers}
                    appendSpaceOnAdd={true}
                    style={{
                        backgroundColor: 'var(--mantine-color-blue-9)',
                        color: 'var(--mantine-color-blue-2)',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontWeight: 'bold',
                    }}
                    displayTransform={(_id: string, display: string) => `@${display}`}
                />
            </MentionsInput>

            <div style={{height: '10px'}} />

            <div>
                <Button
                    className='comment-submit-button'
                    type='submit'
                    disabled={!draft.length}
                >
                    {submitButtonText}
                </Button>
                {props.onCancel && (
                    <Button
                        variant='subtle'
                        type='button'
                        onClick={props.onCancel}
                        style={{marginLeft: '5px'}}
                    >
                        Cancel
                    </Button>
                )}
            </div>

            <div style={{height: '20px'}} />
        </form>
    );
};
